/**
 * MARKETPLACE ITEM DETAIL MODAL
 * 
 * Full-screen detail view for marketplace items
 * Features:
 * - Photo carousel with dots indicator
 * - Full item details (type, finish, collection, etc.)
 * - Sale details (price, condition, quantity, swap status)
 * - Full seller notes
 * - Seller info with trust badges (vouches, member since)
 * - Navigation arrows for prev/next items
 * - Message Seller and Copy to Collection actions
 * - Watch toggle (star icon)
 *
 * UPDATED: Changed from heart to star icon for watching
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ChevronLeft, ChevronRight, MessageSquare, Copy, Check,
  Loader2, Tag, Repeat, Package, Clock, User, Shield, Award,
  ExternalLink, Calendar, Sparkles, Droplets, Grid3X3, Wrench,
  ShoppingBag, History, Gem, Palette, Star, Edit, MapPin
} from 'lucide-react';
import {
  doc, getDoc, collection, addDoc, getDocs, deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import InquiryModal from '../../messaging/components/InquiryModal';
import type { CollectionItem } from '../../types';
import type { AttachedItem } from '../../messaging/types';
import { AppIcons } from '../../constants/icons';
import { CategoryColors, getCategoryColors } from '../../constants/categoryColors';
import { formatPrice } from '../../constants/currencies';
import { getCountryInfo } from '../../constants/countries';

interface MarketplaceItem extends CollectionItem {
  sellerId: string;
  sellerUsername?: string;
}

interface SellerInfo {
  username: string | null;
  avatarUrl: string | null;
  location: string | null;
  shipsTo: string | null;
  memberSince: Date | null;
  vouchCount: number;
  isVerified: boolean;
}

interface MarketplaceItemDetailModalProps {
  item: MarketplaceItem;
  items: MarketplaceItem[]; // All items for navigation
  currentIndex: number;
  currentUserId: string | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
  onCopySuccess?: () => void;
  onEdit?: (item: MarketplaceItem) => void; // Callback to edit own listing
  isWatching?: boolean;
  onWatchChange?: (isWatching: boolean) => void;
}

const MarketplaceItemDetailModal: React.FC<MarketplaceItemDetailModalProps> = ({
  item,
  items,
  currentIndex,
  currentUserId,
  onClose,
  onNavigate,
  onCopySuccess,
  onEdit,
  isWatching: initialIsWatching,
  onWatchChange
}) => {
  // Photo carousel state
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Seller info state
  const [sellerInfo, setSellerInfo] = useState<SellerInfo>({
    username: null,
    avatarUrl: null,
    location: null,
    shipsTo: null,
    memberSince: null,
    vouchCount: 0,
    isVerified: false
  });
  const [loadingSeller, setLoadingSeller] = useState(true);
  
  // Watching state (star icon)
  const [isWatching, setIsWatching] = useState(initialIsWatching ?? false);
  const [watchingLoading, setWatchingLoading] = useState(false);
  
  // Copy to collection state
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Inquiry modal state
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  
  // Login prompt state
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Get all photos
  const getAllPhotos = (): string[] => {
    const photos: string[] = [];
    
    if (item.photos && item.photos.length > 0) {
      // Respect array order (the drag-to-reorder feature persists the
      // user's chosen order). isDefault is now purely for cover-tile
      // selection on cards, not for detail-page display order.
      item.photos.forEach(p => photos.push(p.url));
    }
    
    if (item.swatchPhotos) {
      item.swatchPhotos.forEach(url => {
        if (!photos.includes(url)) photos.push(url);
      });
    }
    
    if (item.bottlePhotos) {
      item.bottlePhotos.forEach(url => {
        if (!photos.includes(url)) photos.push(url);
      });
    }
    
    if (item.photoUrls) {
      item.photoUrls.forEach(url => {
        if (!photos.includes(url)) photos.push(url);
      });
    }
    
    return photos;
  };

  const photos = getAllPhotos();

  // Fetch seller info including vouches
  useEffect(() => {
    const fetchSellerInfo = async () => {
      setLoadingSeller(true);
      try {
        // Get user doc
        const userDoc = await getDoc(doc(db, 'users', item.sellerId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Get vouch count
          const vouchesRef = collection(db, 'users', item.sellerId, 'vouches');
          const vouchesSnapshot = await getDocs(vouchesRef);
          
          // Parse member since date
          let memberSince = null;
          const createdAt = userData.createdAt || userData.profile?.joinedDate;
          if (createdAt) {
            memberSince = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
          }
          
          setSellerInfo({
            username: userData.username || userData.profile?.username || null,
            avatarUrl: userData.avatarUrl || userData.profile?.avatarUrl || null,
            location: userData.location || userData.profile?.location || null,
            shipsTo: userData.shipsTo || userData.profile?.shipsTo || null,
            memberSince,
            vouchCount: vouchesSnapshot.size,
            isVerified: userData.emailVerified || false
          });
        }
      } catch (err) {
        console.error('Error fetching seller info:', err);
      } finally {
        setLoadingSeller(false);
      }
    };

    if (item.sellerId) {
      fetchSellerInfo();
    }
  }, [item.sellerId]);

  // Check watching status (or sync with prop)
  useEffect(() => {
    // If prop is provided, use it
    if (initialIsWatching !== undefined) {
      setIsWatching(initialIsWatching);
      return;
    }
    
    // Otherwise, check from database
    const checkWatching = async () => {
      if (!currentUserId) return;
      
      try {
        const watchingRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
        const snapshot = await getDocs(watchingRef);
        const isOnList = snapshot.docs.some(
          doc => doc.data().itemId === item.id && doc.data().sellerId === item.sellerId
        );
        setIsWatching(isOnList);
      } catch (err) {
        console.error('Error checking watching status:', err);
      }
    };

    checkWatching();
  }, [currentUserId, item.id, item.sellerId, initialIsWatching]);

  // Reset photo index when item changes
  useEffect(() => {
    setCurrentPhotoIndex(0);
    setCopied(false);
  }, [item.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          onNavigate(currentIndex - 1);
        }
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < items.length - 1) {
          onNavigate(currentIndex + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, items.length, onClose, onNavigate]);

  // Toggle watching
  const handleWatchingToggle = async () => {
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    setWatchingLoading(true);
    try {
      const watchingRef = collection(db, 'users', currentUserId, 'marketplaceWishlist');
      
      if (isWatching) {
        const snapshot = await getDocs(watchingRef);
        const docToDelete = snapshot.docs.find(
          doc => doc.data().itemId === item.id && doc.data().sellerId === item.sellerId
        );
        if (docToDelete) {
          await deleteDoc(doc(db, 'users', currentUserId, 'marketplaceWishlist', docToDelete.id));
        }
        setIsWatching(false);
        onWatchChange?.(false);
      } else {
        await addDoc(watchingRef, {
          itemId: item.id,
          sellerId: item.sellerId,
          itemName: item.name,
          itemBrand: item.brand,
          itemType: item.itemType,
          photoUrl: photos[0] || null,
          price: item.destashPrice,
          addedAt: serverTimestamp()
        });
        setIsWatching(true);
        onWatchChange?.(true);
      }
    } catch (err) {
      console.error('Error toggling watching:', err);
    } finally {
      setWatchingLoading(false);
    }
  };

  // Copy to collection
  const handleCopyToCollection = async () => {
    if (!currentUserId) {
      setShowLoginPrompt(true);
      return;
    }

    setCopying(true);

    try {
      // Copy photos WITH source tracking for DMCA
      const copiedPhotos = item.photos?.map(photo => ({
        url: photo.url,
        caption: photo.caption || `${item.brand}/${item.name}`,
        isDefault: photo.isDefault || false,
        // DMCA Source Tracking
        uploadedBy: currentUserId,
        uploadedAt: new Date().toISOString(),
        source: 'marketplace' as const,
        sourceUrl: photo.url,
        sourceUserId: item.sellerId,
        dmcaStatus: 'active' as const,
        // Preserve existing credit if present
        ...(photo.creditName && { creditName: photo.creditName }),
        ...(photo.creditUrl && { creditUrl: photo.creditUrl }),
      })) || [];

      const newItem: Partial<CollectionItem> = {
        name: item.name,
        nameLower: (item.name ?? '').toLowerCase(),
        brand: item.brand,
        brandLower: (item.brand ?? '').toLowerCase(),
        itemType: item.itemType || 'polish',
        colorDescription: item.colorDescription || '',
        photos: copiedPhotos,
        swatchPhotos: item.swatchPhotos || [],
        bottlePhotos: item.bottlePhotos || [],
        photoUrls: item.photoUrls || [],
        polishType: item.polishType || '',
        finish: item.finish || [],
        effects: item.effects || [],
        opacity: item.opacity || '',
        collectionName: item.collectionName || '',
        releaseYear: item.releaseYear || undefined,
        releaseSeason: (item.releaseSeason || undefined) as 'Spring' | 'Summer' | 'Fall' | 'Winter' | undefined,
        releaseType: (item.releaseType || undefined) as 'collection' | 'event' | 'core' | 'unknown' | undefined,
        isRareLE: item.isRareLE || false,
        isFavorite: false,
        onWishlist: false, // Don't add to wishlist - they're copying because they want it, not looking for it
        quantity: 1,
        timesUsed: 0,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
        notes: `Copied from ${sellerInfo.username || 'marketplace'}'s destash`,
        copiedFrom: {
          sellerId: item.sellerId,
          sellerUsername: sellerInfo.username || undefined,
          originalItemId: item.id,
          copiedAt: new Date().toISOString()
        }
      };

      const itemsRef = collection(db, 'users', currentUserId, 'items');
      await addDoc(itemsRef, newItem);

      setCopied(true);
      onCopySuccess?.();
      
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Error copying to collection:', error);
      alert('Failed to copy item. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  // Get attached item for inquiry
  const getAttachedItem = (): AttachedItem => ({
    itemId: item.id,
    ownerId: item.sellerId,
    name: item.name,
    brand: item.brand,
    photoUrl: photos[0] || undefined,
  });

  // Calculate time since listed
  const getTimeAgo = (): string | null => {
    const listedDate = item.destashDate || item.updatedAt || item.createdAt;
    if (!listedDate) return null;

    const date = (listedDate as any).toDate ? (listedDate as any).toDate() : new Date(listedDate);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Listed today';
    if (diffDays === 1) return 'Listed yesterday';
    if (diffDays < 7) return `Listed ${diffDays} days ago`;
    if (diffDays < 30) return `Listed ${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `Listed ${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `Listed ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  };

  // Format member since
  const getMemberSince = (): string => {
    if (!sellerInfo.memberSince) return '';
    const options: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric' };
    return sellerInfo.memberSince.toLocaleDateString('en-US', options);
  };

  // Get status styling
  const getStatusStyle = () => {
    const status = item.destashStatus || 'available';
    switch (status) {
      case 'available':
        return { bg: 'bg-green-100', text: 'text-green-700', label: 'Available' };
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' };
      case 'sold':
        return { bg: 'border-2 border-gray-300', text: 'text-gray-600', label: 'Sold' };
      default:
        return { bg: 'bg-green-100', text: 'text-green-700', label: 'Available' };
    }
  };

  // Get condition styling
  const getConditionStyle = () => {
    switch (item.destashCondition) {
      case 'New':
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-700',
          label: 'New/Sealed',
          icon: <Sparkles className="w-4 h-4 text-current" aria-label="New" />
        };
      case 'Swatched Only':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          label: 'Swatched Only',
          icon: <Palette className="w-4 h-4 text-current" aria-label="Swatched" />
        };
      case 'Used - Light Use':
      case 'Used - Moderate':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-700',
          label: 'Used',
          icon: <AppIcons.Archive className="w-4 h-4 text-current" aria-label="Used" />
        };
      default:
        return null;
    }
  };

  // Get type badge color
  const getTypeBadgeColor = () => {
    const colors = getCategoryColors(item.itemType);
    return colors.bg;
  };

  const statusStyle = getStatusStyle();
  const conditionStyle = getConditionStyle();
  const isSold = item.destashStatus === 'sold';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
        <div className="modal-light max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="modal-header-light">
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 ${getTypeBadgeColor()} text-white text-sm font-bold rounded-full capitalize`}>
                {item.itemType || 'Polish'}
              </span>
              <span className={`px-3 py-1 ${statusStyle.bg} ${statusStyle.text} text-sm font-medium rounded-full`}>
                {statusStyle.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Watch button */}
              {!isSold && (
                <button
                  onClick={handleWatchingToggle}
                  disabled={watchingLoading}
                  className={`p-2 rounded-full transition-all ${
                    isWatching
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'border-2 border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-amber-500'
                  }`}
                  title={isWatching ? 'Stop watching' : 'Wishlist/Watchlist'}
                >
                  {watchingLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Star className={`w-5 h-5 ${isWatching ? 'fill-current' : ''}`} />
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-50 rounded-full transition-default"
              >
                <X className="icon-base text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="md:flex">
              {/* Photo Section */}
              <div className="md:w-1/2 p-6 border border-gray-200">
                {/* Main Photo */}
                <div className="relative aspect-square bg-white rounded-xl overflow-hidden shadow-lg mb-4">
                  {photos.length > 0 ? (
                    <img
                      src={photos[currentPhotoIndex]}
                      alt={item.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-fuchsia-200 to-purple-200 flex items-center justify-center">
                      <Palette className="w-32 h-32 text-white opacity-50" aria-label="No image" />
                    </div>
                  )}

                  {/* Photo navigation */}
                  {photos.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentPhotoIndex(prev => prev > 0 ? prev - 1 : photos.length - 1)}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-default"
                      >
                        <ChevronLeft className="icon-md text-gray-600" />
                      </button>
                      <button
                        onClick={() => setCurrentPhotoIndex(prev => prev < photos.length - 1 ? prev + 1 : 0)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 rounded-full shadow-lg hover:bg-white transition-default"
                      >
                        <ChevronRight className="icon-md text-gray-600" />
                      </button>
                    </>
                  )}

                  {/* Sold overlay */}
                  {isSold && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="bg-white text-gray-900 px-6 py-3 rounded-full font-bold text-xl">
                        SOLD
                      </span>
                    </div>
                  )}
                </div>

                {/* Photo dots */}
                {photos.length > 1 && (
                  <div className="flex justify-center gap-2">
                    {photos.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhotoIndex(idx)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                          idx === currentPhotoIndex
                            ? 'bg-indigo-600 w-6'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                )}

                {/* Thumbnail strip */}
                {photos.length > 1 && (
                  <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                    {photos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPhotoIndex(idx)}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentPhotoIndex
                            ? 'border border-gray-300 shadow-md'
                            : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img src={photo} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Details Section */}
              <div className="md:w-1/2 p-6">
                {/* Brand & Name */}
                <div className="mb-4">
                  <h3 className="text-sm uppercase text-light-primary font-bold tracking-wide mb-1 font-heading">
                    {item.brand}
                  </h3>
                  <h2 className="text-2xl font-bold text-light-primary font-body">
                    {item.name}
                  </h2>
                </div>

                {/* Color Description */}
                {item.colorDescription && (
                  <p className="text-light-secondary mb-4 font-body">
                    {item.colorDescription}
                  </p>
                )}

                {/* POLISH-SPECIFIC DETAILS */}
                {item.itemType === 'polish' && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-300">
                    <h4 className="text-sm font-bold text-light-primary mb-3 flex items-center gap-2">
                      <Droplets className="icon-sm" />
                      Polish Details
                    </h4>
                    <div className="space-y-2">
                      {item.polishType && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Type:</span>
                          <span className="text-light-primary font-medium capitalize">{item.polishType.replace(/-/g, ' ')}</span>
                        </div>
                      )}
                      {item.finish && item.finish.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Finish:</span>
                          <span className="text-light-primary font-medium capitalize">{item.finish.join(', ')}</span>
                        </div>
                      )}
                      {item.effects && item.effects.length > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Effects:</span>
                          <span className="text-light-primary font-medium capitalize">{item.effects.join(', ')}</span>
                        </div>
                      )}
                      {item.opacity && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Opacity:</span>
                          <span className="text-light-primary font-medium capitalize">{item.opacity}</span>
                        </div>
                      )}
                      {item.topperType && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Topper Type:</span>
                          <span className="text-light-primary font-medium capitalize">{item.topperType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* PLATE-SPECIFIC DETAILS */}
                {item.itemType === 'plate' && (
                  <div className={`${CategoryColors.plate.light} rounded-xl p-4 mb-4 border ${CategoryColors.plate.lightBorder}`}>
                    <h4 className={`text-sm font-bold ${CategoryColors.plate.text} mb-3 flex items-center gap-2`}>
                      <Grid3X3 className="w-4 h-4" />
                      Plate Details
                    </h4>
                    <div className="space-y-2">
                      {item.plateSize && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Size:</span>
                          <span className="text-light-primary font-medium capitalize">{item.plateSize}</span>
                        </div>
                      )}
                      {item.imageCount && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Images:</span>
                          <span className="text-light-primary font-medium">{item.imageCount} designs</span>
                        </div>
                      )}
                      {item.plateStyle && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Style:</span>
                          <span className="text-light-primary font-medium capitalize">{item.plateStyle}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* EMBELLISHMENT-SPECIFIC DETAILS */}
                {item.itemType === 'embellishment' && (
                  <div className={`${CategoryColors.embellishment.light} rounded-xl p-4 mb-4 border ${CategoryColors.embellishment.lightBorder}`}>
                    <h4 className={`text-sm font-bold ${CategoryColors.embellishment.text} mb-3 flex items-center gap-2`}>
                      <Sparkles className="icon-sm" />
                      Embellishment Details
                    </h4>
                    <div className="space-y-2">
                      {item.embellishmentType && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Type:</span>
                          <span className="text-light-primary font-medium capitalize">{item.embellishmentType}</span>
                        </div>
                      )}
                      {item.material && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Material:</span>
                          <span className="text-light-primary font-medium capitalize">{item.material}</span>
                        </div>
                      )}
                      {item.size && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Size:</span>
                          <span className="text-light-primary font-medium">{item.size}</span>
                        </div>
                      )}
                      {item.shape && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Shape:</span>
                          <span className="text-light-primary font-medium capitalize">{item.shape}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TOOL-SPECIFIC DETAILS */}
                {item.itemType === 'tool' && (
                  <div className={`${CategoryColors.tool.light} rounded-xl p-4 mb-4 border ${CategoryColors.tool.lightBorder}`}>
                    <h4 className={`text-sm font-bold ${CategoryColors.tool.text} mb-3 flex items-center gap-2`}>
                      <Wrench className="icon-sm" />
                      Tool Details
                    </h4>
                    <div className="space-y-2">
                      {item.toolType && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 w-28">Type:</span>
                          <span className="text-gray-900 font-medium capitalize">{item.toolType}</span>
                        </div>
                      )}
                      {item.powerSource && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-500 w-28">Power:</span>
                          <span className="text-gray-900 font-medium capitalize">{item.powerSource}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* COLLECTION & RELEASE INFO (shared across types) */}
                {(item.collectionName || item.releaseYear || item.releaseSeason || item.isRareLE) && (
                  <div className="space-y-2 mb-4">
                    {item.collectionName && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-light-muted w-28">Collection:</span>
                        <span className="text-light-primary font-medium">{item.collectionName}</span>
                      </div>
                    )}
                    {(item.releaseYear || item.releaseSeason) && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-light-muted w-28">Released:</span>
                        <span className="text-light-primary font-medium">
                          {[item.releaseSeason, item.releaseYear].filter(Boolean).join(' ')}
                        </span>
                      </div>
                    )}
                    {item.isRareLE && (
                      <div className="mt-2">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full flex items-center gap-1 inline-flex">
                          <Gem className="w-3 h-3 text-current" aria-label="Rare/Limited Edition" />
                          Rare/Limited Edition
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* USAGE & HISTORY (shows how the seller used it) */}
                {(item.timesUsed !== undefined || item.lastUsedDate || item.purchaseDate || item.notes) && (
                  <div className="border border-gray-200 rounded-xl p-4 mb-4 border border-gray-300">
                    <h4 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2">
                      <History className="icon-sm" />
                      Usage History
                    </h4>
                    <div className="space-y-2">
                      {item.timesUsed !== undefined && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Times Used:</span>
                          <span className="text-light-primary font-medium">
                            {item.timesUsed === 0 ? 'Never used' : `${item.timesUsed} time${item.timesUsed !== 1 ? 's' : ''}`}
                          </span>
                        </div>
                      )}
                      {item.lastUsedDate && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Last Used:</span>
                          <span className="text-light-primary font-medium">
                            {(() => {
                              const date = new Date(item.lastUsedDate);
                              return isNaN(date.getTime()) 
                                ? 'Unknown Date' 
                                : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            })()}
                          </span>
                        </div>
                      )}
                      {item.purchaseDate && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Purchased:</span>
                          <span className="text-light-primary font-medium flex items-center gap-1">
                            <ShoppingBag className="icon-xs" />
                            {(() => {
                              const date = new Date(item.purchaseDate);
                              return isNaN(date.getTime()) 
                                ? 'Unknown Date' 
                                : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            })()}
                          </span>
                        </div>
                      )}
                      {item.purchaseSource && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-light-muted w-28">Source:</span>
                          <span className="text-light-primary font-medium capitalize">{item.purchaseSource}</span>
                        </div>
                      )}
                    </div>
                    {/* Public notes about the item */}
                    {item.notes && (
                      <div className="mt-3 pt-3 border-t border border-gray-300">
                        <p className="text-sm text-light-secondary italic">"{item.notes}"</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Sale Details */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-100">
                  <h4 className="text-sm font-bold text-light-primary mb-3 flex items-center gap-2">
                    <Tag className="icon-sm" />
                    Sale Details
                  </h4>
                  
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {/* Price - only show if there's an actual price */}
                    {(item.destashPrice ?? 0) > 0 && (
                      <span className={`px-4 py-2 rounded-full text-lg font-bold ${
                        isSold ? 'bg-gray-200 text-gray-500 line-through' : 'bg-green-500 text-white'
                      }`}>
                        {formatPrice(item.destashPrice, item.destashCurrency)}
                      </span>
                    )}
                    
                    {/* Trade Only - when swaps enabled but no price */}
                    {item.isOpenToSwaps && (!item.destashPrice || item.destashPrice === 0) && !isSold && (
                      <span className="px-4 py-2 rounded-full text-lg font-bold bg-purple-500 text-white flex items-center gap-2">
                        <Repeat className="icon-sm" />
                        Trade Only
                      </span>
                    )}

                    {/* Condition */}
                    {conditionStyle && (
                      <span className={`px-3 py-1.5 ${conditionStyle.bg} ${conditionStyle.text} rounded-full text-sm font-medium flex items-center gap-1 inline-flex`}>
                        {conditionStyle.icon}
                        {conditionStyle.label}
                      </span>
                    )}
                    
                    {/* Quantity - always show, default to 1 */}
                    <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium flex items-center gap-1">
                      <Package className="icon-sm" />
                      Qty: {item.destashQuantity || 1}
                    </span>
                    
                    {/* Open to Trades - only show when there's BOTH a price AND swaps enabled */}
                    {item.isOpenToSwaps && (item.destashPrice ?? 0) > 0 && (
                      <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                        <Repeat className="icon-sm" />
                        Open to Trades
                      </span>
                    )}
                  </div>

                  {/* Ships To (structured field) */}
                  {(item.destashShipsToWorldwide || (item.destashShipsToCountries && item.destashShipsToCountries.length > 0)) && (
                    <div className="flex items-center gap-1 text-sm text-light-secondary mb-3 font-body">
                      <MapPin className="icon-sm" />
                      <span className="font-medium">Ships to:</span>
                      <span>
                        {item.destashShipsToWorldwide
                          ? 'Worldwide'
                          : (item.destashShipsToCountries ?? [])
                              .map((code) => getCountryInfo(code)?.name || code)
                              .join(', ')}
                      </span>
                    </div>
                  )}

                  {/* Time listed */}
                  {getTimeAgo() && !isSold && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                      <Clock className="icon-sm" />
                      {getTimeAgo()}
                    </div>
                  )}

                  {/* Seller notes */}
                  {item.destashNotes && (
                    <div className="bg-white/70 rounded-lg p-3">
                      <p className="text-sm text-light-secondary font-medium mb-1">Seller Notes:</p>
                      <p className="text-light-primary font-body">
                        "{item.destashNotes}"
                      </p>
                    </div>
                  )}
                </div>

                {/* Seller Info */}
                <div className="border border-gray-200 rounded-xl p-4 mb-6">
                  <h4 className="text-sm font-bold text-light-secondary mb-3 flex items-center gap-2">
                    <User className="icon-sm" />
                    Seller
                  </h4>
                  
                  {loadingSeller ? (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2" />
                        <div className="h-3 bg-gray-200 rounded animate-pulse w-32" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <Link
                        to={sellerInfo.username ? `/profile/${sellerInfo.username}` : '#'}
                        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                      >
                        {sellerInfo.avatarUrl ? (
                          <img
                            src={sellerInfo.avatarUrl}
                            alt={sellerInfo.username || ''}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full rainbow-gradient flex items-center justify-center text-white text-lg font-bold shadow">
                            {sellerInfo.username?.charAt(0).toUpperCase() || '?'}
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-light-primary font-body">
                            @{sellerInfo.username || 'Unknown'}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-light-muted">
                            {sellerInfo.memberSince && (
                              <span className="flex items-center gap-1">
                                <Calendar className="icon-xs" />
                                Member since {getMemberSince()}
                              </span>
                            )}
                            {sellerInfo.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="icon-xs" />
                                Ships from {sellerInfo.location}
                              </span>
                            )}
                            {sellerInfo.shipsTo && (
                              <span className="flex items-center gap-1">
                                <Package className="icon-xs" />
                                Ships to {sellerInfo.shipsTo}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                      
                      {/* Trust badges */}
                      <div className="flex flex-col items-end gap-1">
                        {sellerInfo.vouchCount > 0 && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            <Award className="icon-xs" />
                            {sellerInfo.vouchCount} vouch{sellerInfo.vouchCount !== 1 ? 'es' : ''}
                          </span>
                        )}
                        {sellerInfo.isVerified && (
                          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            <Shield className="icon-xs" />
                            Verified
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {sellerInfo.username && (
                    <div className="mt-3 flex flex-col gap-2">
                      <Link
                        to={`/profile/${sellerInfo.username}`}
                        className="flex items-center justify-center gap-1 text-sm text-gray-600 hover:text-gray-900 font-medium"
                      >
                        View Full Profile
                        <ExternalLink className="icon-sm" />
                      </Link>
                      <Link
                        to={`/destash/${sellerInfo.username}`}
                        className="flex items-center justify-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-medium"
                      >
                        <ShoppingBag className="icon-sm" />
                        More items from @{sellerInfo.username}
                      </Link>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {!isSold ? (
                  currentUserId === item.sellerId ? (
                    /* Owner is viewing their own listing - show Edit button */
                    <div className="flex gap-3">
                      <button
                        onClick={() => onEdit?.(item)}
                        disabled={!onEdit}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 font-body ${
                          onEdit
                            ? 'btn-primary shadow-lg hover:shadow-xl'
                            : 'bg-gray-200 text-light-muted cursor-not-allowed'
                        }`}
                      >
                        <Edit className="icon-md" />
                        Edit Listing
                      </button>
                    </div>
                  ) : (
                    /* Non-owner viewing - show Message and Copy buttons */
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowInquiryModal(true)}
                        disabled={!currentUserId}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 font-body ${
                          !currentUserId
                            ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                            : 'btn-primary shadow-lg hover:shadow-xl'
                        }`}
                      >
                        <MessageSquare className="icon-md" />
                        Message Seller
                      </button>

                      <button
                        onClick={handleCopyToCollection}
                        disabled={copying || copied || !currentUserId}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 font-body ${
                          copied
                            ? 'bg-green-500 text-white'
                            : copying
                              ? 'bg-gray-300 text-gray-500 cursor-wait'
                              : !currentUserId
                                ? 'bg-gray-200 text-light-muted cursor-not-allowed'
                                : 'bg-white border-2 border-gray-300 text-light-primary hover:bg-gray-100'
                        }`}
                      >
                        {copied ? (
                          <>
                            <Check className="icon-md" />
                            Added!
                          </>
                        ) : copying ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Copy className="icon-md" />
                            Copy to Collection
                          </>
                        )}
                      </button>
                    </div>
                  )
                ) : (
                  <div className="text-center py-4 text-light-muted font-body">
                    This item has been sold
                  </div>
                )}

                {!currentUserId && !isSold && (
                  <p className="text-center text-sm text-light-muted mt-3 font-body">
                    Sign in to message seller or add to your collection
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Footer Navigation */}
          <div className="flex items-center justify-between px-6 py-4 border-t border border-gray-300 border border-gray-200">
            <button
              onClick={() => onNavigate(currentIndex - 1)}
              disabled={currentIndex === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
                currentIndex === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-light-secondary hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="icon-md" />
              Previous
            </button>
            
            <span className="text-sm text-gray-500">
              {currentIndex + 1} of {items.length}
            </span>
            
            <button
              onClick={() => onNavigate(currentIndex + 1)}
              disabled={currentIndex === items.length - 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-default ${
                currentIndex === items.length - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-light-secondary hover:bg-gray-200'
              }`}
            >
              Next
              <ChevronRight className="icon-md" />
            </button>
          </div>
        </div>
      </div>

      {/* Inquiry Modal */}
      {currentUserId && (
        <InquiryModal
          isOpen={showInquiryModal}
          onClose={() => setShowInquiryModal(false)}
          currentUserId={currentUserId}
          sellerId={item.sellerId}
          sellerUsername={sellerInfo.username}
          items={[getAttachedItem()]}
        />
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[160]">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 text-center">
            <div className="mb-4 flex justify-center">
              <Shield className="w-20 h-20 text-gray-400" aria-label="Locked" />
            </div>
            <h3 className="text-xl font-bold text-light-primary mb-2 font-heading">
              Sign In Required
            </h3>
            <p className="text-light-secondary mb-4 font-body">
              You need to be signed in to save items to your wishlist or collection.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-light-secondary rounded-lg hover:bg-gray-200 transition-default font-body"
              >
                Cancel
              </button>
              <Link
                to="/auth"
                className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-default text-center font-body"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplaceItemDetailModal;
